export interface PipelineFileMeta {
  fileId: string;
  fileName: string;
  schemaId: string;
  /** Server-resolved display name (board-state); preferred over client cache. */
  schemaName?: string | null;
  reportType?: string | null;
  allowedReportTypes?: string[];
  /** Provider the file was uploaded for; null/omit when unassigned. */
  providerId?: string | null;
  /** Uploader Entra oid (or legacy email); used for avatar colour/letter. */
  uploadedBy?: string | null;
  /** kodex_files.created_at; cards sort ascending on it (missing → last). */
  createdAt?: string | null;
}

export interface PendingPipelineUpload {
  file: File;
  schemaId: string;
  reportType?: string | null;
  allowedReportTypes?: string[];
  detectedType?: string | null;
  isClassifying?: boolean;
}

export function isPendingPipelineUploadReady(
  entry: PendingPipelineUpload,
  schemaNameById: Record<string, string>,
  requiresReportType: (schemaName: string) => boolean
): boolean {
  if (!entry.schemaId.trim()) return false;
  if (entry.isClassifying) return false;
  const schemaName = schemaNameById[entry.schemaId] ?? entry.schemaId;
  if (!requiresReportType(schemaName)) return true;
  return !!entry.reportType && entry.reportType !== "unknown";
}

export function canSubmitPendingPipelineUploads(
  entries: PendingPipelineUpload[],
  schemaNameById: Record<string, string>,
  requiresReportType: (schemaName: string) => boolean
): boolean {
  return (
    entries.length > 0 &&
    entries.every((entry) =>
      isPendingPipelineUploadReady(entry, schemaNameById, requiresReportType)
    )
  );
}

const MISSING_SCHEMA_LABEL = "—";

/**
 * Human schema label for board cards. Never returns a raw schemaId/UUID.
 * A cache value equal to schemaId is treated as missing.
 */
export function humanSchemaName(
  schemaId: string | undefined,
  schemaName?: string | null,
  schemaNameById: Record<string, string> = {}
): string | null {
  const id = schemaId?.trim() ?? "";
  const stamped = schemaName?.trim();
  if (stamped && stamped !== id) return stamped;
  if (id) {
    const cached = schemaNameById[id]?.trim();
    if (cached && cached !== id) return cached;
  }
  return null;
}

export function resolvePipelineSchemaDisplayName(
  schemaId: string | undefined,
  schemaName?: string | null,
  schemaNameById: Record<string, string> = {}
): string {
  return humanSchemaName(schemaId, schemaName, schemaNameById) ?? MISSING_SCHEMA_LABEL;
}

export function buildSchemaNameByFileId(
  fileMeta: PipelineFileMeta[],
  schemaNameById: Record<string, string>
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const entry of fileMeta) {
    result[entry.fileId] = resolvePipelineSchemaDisplayName(
      entry.schemaId,
      entry.schemaName,
      schemaNameById
    );
  }
  return result;
}

/** Prefer incoming schemaName; keep the existing stamp when the server sends blank. */
export function mergePipelineFileMetaSchemaNames(
  incoming: PipelineFileMeta[],
  existing: PipelineFileMeta[]
): PipelineFileMeta[] {
  const existingNameById = new Map(
    existing.map((entry) => [entry.fileId, entry.schemaName?.trim() || null])
  );
  return incoming.map((file) => {
    const incomingName = file.schemaName?.trim();
    if (incomingName) {
      return { ...file, schemaName: incomingName };
    }
    return { ...file, schemaName: existingNameById.get(file.fileId) || null };
  });
}

export function summarizePipelineSchemas(schemaNames: string[]): string {
  const unique = [...new Set(schemaNames.filter(Boolean))];
  if (unique.length === 0) return "—";
  if (unique.length === 1) return unique[0]!;
  if (unique.length === 2) return unique.join(" · ");
  return `${unique.length} schemas`;
}
